import React, { FC, ReactNode } from "react";

import { css } from "@emotion/react";
import styled from "@emotion/styled";

import colors from "../../../../utils/colors";
import { withField as withFieldWrapper } from "../Field";
import icon from "./icon.svg";

const Container = styled.div`
    position: relative;
    margin-top: 5px;
    margin-bottom: 5px;

    &::after {
        position: absolute;
        top: 12px;
        right: 10px;
        z-index: 10;
        width: 16px;
        height: 16px;
        pointer-events: none;
        content: "";
        background-image: url("${icon}");
        transform: rotate(90deg);
    }
`;

const Select = styled.select`
    width: 100%;
    max-width: 100%;
    padding: 15px 35px 5px 10px;
    font-size: 1em;
    line-height: 20px;
    color: ${colors.blue};
    background-color: white;
    border-color: ${colors.darkGrey};
    border-style: solid;
    border-width: 1px;
    border-radius: 4px;
    appearance: none;

    &:disabled {
        color: #bebebe;
        border-color: #bebebe;
    }

    &:focus ~ span {
        align-items: flex-start;
        font-size: 0.75em;
    }

    &:not(:placeholder-shown) ~ span {
        align-items: flex-start;
        font-size: 0.75em;
    }

    &:hover {
        cursor: pointer;
    }
`;

const Placeholder = styled.span`
    position: absolute;
    top: 2px;
    left: 11px;
    display: flex;
    align-items: center;
    height: 100%;
    overflow: visible !important;
    font-size: 1rem;
    color: ${colors.darkGrey};
    pointer-events: none;
    transition: 0.2s ease all;
`;

export type NewSelectProps<a extends string = string> = {
    options: ReadonlyArray<{
        value: a;
        text: ReactNode;
    }>;
    fullwidth?: boolean;
    withField?: boolean;
    minWidth?: string;
    withDefault?: boolean;
    value: a;
    id?: string | null;
} & Omit<JSX.IntrinsicElements["select"], "id" | "value">;

const NewSelect = <a extends string = string>({
    options,
    name,
    id,
    placeholder = "Bitte wählen",
    fullwidth = false,
    withField = false,
    minWidth,
    withDefault = false,
    ...props
}: NewSelectProps<a>) => {
    const component = (
        <Container
            css={css`
                ${fullwidth ? "width: 100%;" : ""}
            `}
        >
            <Select
                name={name}
                id={id ?? name}
                css={css`
                    ${fullwidth ? "width: 100%;" : ""}
                    ${minWidth ? `min-width: ${minWidth};` : ""}
                `}
                {...props}
            >
                {withDefault && <option value="">Bitte wählen</option>}
                {options.map((option, index) => (
                    <option key={index} value={option.value}>
                        {option.text}
                    </option>
                ))}
            </Select>
            <Placeholder>{placeholder}</Placeholder>
        </Container>
    );

    return withField ? withFieldWrapper(component) : component;
};
export default NewSelect;

export const SalutationSelect = <a extends string>({
    options = [
        { value: "Herr" as const, text: "Herr" },
        { value: "Frau" as const, text: "Frau" },
        { value: "Divers" as const, text: "Divers" },
    ],
    placeholder = "Anrede",
    ...props
}: NewSelectProps<a | "Herr" | "Frau" | "Divers">) => (
    <NewSelect {...{ ...props, options, placeholder }} />
);
